// How the character clips did on this screen, step by step (TAS-557). Mel is
// judging full-rate (24 fps) clips on a staging game before deciding on smaller
// copies, and nothing reported how they loaded on each phone: only the 3D
// bottle sent anything ('Bottle Rendered').
//
// Every clip comes through one loader, loadEmoteBlob (emoteSystem.jsx), which
// reports each ask here and how it ended. Deadlines stay with the callers: one
// that gives up and keeps the still says so with clipGaveUp. The game shell and
// the TV mount useAvatarClipSummary, which sends one 'Avatar Clips Summary' as
// each step ends, and again when the page is hidden or the screen goes away.
//
//   clips_requested    asks made through the loader
//   clips_from_memory  of those, answered at once from the in-memory blob
//   clips_loaded       asks the clip arrived for (memory answers included)
//   clips_timed_out    asks a caller gave up on at its deadline and kept the
//                      still; a clip that arrived but hadn't decoded in time
//                      moves here from clips_loaded
//   clips_aborted      asks withdrawn before the clip arrived for any other
//                      reason (the screen went, the step moved on)
//   clips_failed       asks whose download failed
//   load_ms_p50/p95/max  ask to arrival, for asks that waited on a download.
//                      An ask given up at its deadline still counts if the
//                      download finishes anyway, so the slow tail shows.
//   bytes_transferred, cache_hits  resource timing for the clip files the
//                      loader fetched: transferSize, and 0 bytes with a body
//                      (the HTTP cache). Null where resource timing isn't read.
//
// Counts are what happened while the step was up. A clip asked for at the end
// of one step that lands in the next is asked in the first and loaded in the
// second, so the counts add up over a game, not always within one event.
//
// Nothing here may throw into the loader or a screen, and it holds no timers.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { track } from '@tasted/utils/analytics';
import { deviceTelemetry } from './bottleModels';
import { BEACON } from './pageVisibility';

export const CLIP_SUMMARY_EVENT = 'Avatar Clips Summary';

// A busy step asks for a few dozen clips; this only bounds a screen left open.
const MAX_SAMPLES = 500;
// Asks a caller may still give up on, newest last.
const MAX_RECENT = 200;

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

function freshWindow() {
  return { requested: 0, fromMemory: 0, loaded: 0, timedOut: 0, aborted: 0, failed: 0, samples: [], bytes: 0, cacheHits: 0 };
}

let win = freshWindow();
let recent = [];
// Absolute URLs of the clips the loader has fetched. The built files lose
// their folder (/assets/dino-idle-<hash>.webp), so resource entries are
// matched against these rather than an avatar-motion path.
const clipUrls = new Set();
let observer = null;
let observers = 0;
let observedBefore = false;
// Numbers each summary, so track()'s 5 s dedupe never drops a real one.
let seq = 0;

function rememberUrl(src) {
  try {
    clipUrls.add(new URL(src, document.baseURI).href);
  } catch { /* not a URL resource timing would report */ }
}

function forget(req) {
  const i = recent.indexOf(req);
  if (i !== -1) recent.splice(i, 1);
}

/**
 * The loader's side: one ask for `src`. `signal` is the caller's, if any;
 * `inMemory` an ask answered at once from the blob already in hand. Returns
 * the handle for clipSettled.
 */
export function clipRequested(src, { signal, inMemory = false } = {}) {
  try {
    const req = { src, signal: signal || null, state: inMemory ? 'loaded' : 'pending', at: now(), countedIn: win };
    win.requested += 1;
    if (inMemory) {
      win.fromMemory += 1;
      win.loaded += 1;
    } else {
      rememberUrl(src);
    }
    recent.push(req);
    if (recent.length > MAX_RECENT) recent.shift();
    return req;
  } catch {
    return null;
  }
}

/** The loader's side: how an ask ended, 'loaded' | 'failed' | 'aborted'. A second report is ignored. */
export function clipSettled(req, outcome) {
  try {
    if (!req) return;
    if (outcome === 'loaded' && (req.state === 'pending' || req.state === 'timed_out') && win.samples.length < MAX_SAMPLES) {
      win.samples.push(now() - req.at);
    }
    if (req.state !== 'pending') return;
    req.state = outcome;
    req.countedIn = win;
    if (outcome === 'loaded') win.loaded += 1;
    else if (outcome === 'failed') win.failed += 1;
    else win.aborted += 1;
    if (outcome !== 'loaded') forget(req);
  } catch { /* stats never break a load */ }
}

/**
 * A caller passed its deadline and keeps the still. `handle` is the
 * AbortSignal it gave the loader (call this before aborting it), or, for a
 * caller that passed no signal, the clip's src.
 */
export function clipGaveUp(handle) {
  try {
    if (!handle) return;
    const open = (r) => r.state === 'pending' || r.state === 'loaded';
    let gaveUp = [];
    if (typeof handle === 'string') {
      // The newest open ask: the one this caller just made. (No findLast: older webviews lack it.)
      for (let i = recent.length - 1; i >= 0; i -= 1) {
        const r = recent[i];
        if (!r.signal && r.src === handle && open(r)) { gaveUp = [r]; break; }
      }
    } else {
      gaveUp = recent.filter((r) => r.signal === handle && open(r));
    }
    gaveUp.forEach((req) => {
      // It arrived but hadn't shown by the deadline (still decoding).
      if (req.state === 'loaded' && req.countedIn === win) win.loaded -= 1;
      req.state = 'timed_out';
      req.countedIn = win;
      win.timedOut += 1;
      forget(req);
    });
  } catch { /* stats never break a screen */ }
}

function countEntries(entries) {
  for (const entry of entries) {
    if (!clipUrls.has(entry.name) || typeof entry.transferSize !== 'number') continue;
    if (entry.transferSize > 0) win.bytes += entry.transferSize;
    else if (entry.decodedBodySize > 0) win.cacheHits += 1;
  }
}

/**
 * Start reading resource timing for the clip files. An observer is handed
 * every entry, so it doesn't miss the late clips when the browser's buffer
 * fills (TAS-526; netTier empties that buffer when it does). The first start
 * also takes what the buffer already holds. Returns the stop function.
 */
export function startClipTiming() {
  observers += 1;
  if (!observer) {
    try {
      if (typeof PerformanceObserver === 'function') {
        const next = new PerformanceObserver((list) => {
          try { countEntries(list.getEntries()); } catch { /* ignore */ }
        });
        next.observe({ type: 'resource', buffered: !observedBefore });
        observer = next;
        observedBefore = true;
      }
    } catch {
      observer = null;
    }
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    observers = Math.max(0, observers - 1);
    if (observers > 0 || !observer) return;
    try {
      countEntries(observer.takeRecords?.() ?? []);
      observer.disconnect();
    } catch { /* ignore */ }
    observer = null;
  };
}

// Nearest rank, in whole ms.
function percentile(sorted, q) {
  if (!sorted.length) return null;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1));
  return Math.round(sorted[i]);
}

/** Close the current window and start the next. Returns the closed one. */
export function closeClipWindow() {
  try {
    if (observer) countEntries(observer.takeRecords?.() ?? []);
  } catch { /* ignore */ }
  const closed = { ...win, timed: Boolean(observer) };
  win = freshWindow();
  return closed;
}

/**
 * Send a closed window. Nothing is sent when no clip was asked for or settled
 * in it. Returns the properties sent, or null.
 */
export function sendClipSummary(closed, { surface, stepIndex, stepType, sessionCode } = {}, reason = 'step-end') {
  try {
    if (!closed) return null;
    const settled = closed.loaded + closed.timedOut + closed.aborted + closed.failed;
    if (!closed.requested && !settled) return null;
    const sorted = [...closed.samples].sort((a, b) => a - b);
    seq += 1;
    const props = {
      surface: surface || null,
      session_id: sessionCode || null,
      step_index: typeof stepIndex === 'number' ? stepIndex : null,
      step_type: stepType || null,
      flush_reason: reason,
      clips_requested: closed.requested,
      clips_from_memory: closed.fromMemory,
      clips_loaded: closed.loaded,
      clips_timed_out: closed.timedOut,
      clips_aborted: closed.aborted,
      clips_failed: closed.failed,
      load_ms_p50: percentile(sorted, 0.5),
      load_ms_p95: percentile(sorted, 0.95),
      load_ms_max: sorted.length ? Math.round(sorted[sorted.length - 1]) : null,
      bytes_transferred: closed.timed ? closed.bytes : null,
      cache_hits: closed.timed ? closed.cacheHits : null,
      ...deviceTelemetry(),
      dedupeKey: `clips-${seq}`,
    };
    // A page going away gets the beacon, or the event waits for a load that may never come.
    track(CLIP_SUMMARY_EVENT, props, reason === 'page-hidden' ? BEACON : undefined);
    return props;
  } catch {
    return null;
  }
}

/** Close the window and send it at once. */
export function flushClipSummary(step, reason) {
  return sendClipSummary(closeClipWindow(), step, reason);
}

/**
 * Mounted once by the game shell (surface 'phone') and the TV ('tv'). Sends
 * the step that just ended when stepIndex moves on, and flushes when the page
 * is hidden (iOS fires visibilitychange and pagehide together; the second
 * finds the window empty) or the screen unmounts.
 */
export function useAvatarClipSummary({ surface, stepIndex, stepType, sessionCode }) {
  const stepRef = useRef(null);
  const endedRef = useRef(null);

  useEffect(() => {
    const stopTiming = startClipTiming();
    const flush = (reason) => flushClipSummary({ surface, ...stepRef.current }, reason);
    const onHide = () => flush('page-hidden');
    const onVisibility = () => { if (document.visibilityState === 'hidden') onHide(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      flush('unmount');
      stopTiming();
    };
  }, [surface]);

  // The window is cut before the commit's passive effects run: the TV mounts
  // the next step's screen in the same commit, and its clip asks belong to
  // that step. Sending waits for the passive effect below, off the paint.
  useLayoutEffect(() => {
    const ended = stepRef.current;
    stepRef.current = { stepIndex, stepType, sessionCode };
    if (ended && ended.stepIndex !== stepIndex) endedRef.current = { step: ended, closed: closeClipWindow() };
  }, [stepIndex, stepType, sessionCode]);

  useEffect(() => {
    const ended = endedRef.current;
    if (!ended) return;
    endedRef.current = null;
    sendClipSummary(ended.closed, { surface, ...ended.step }, 'step-end');
  }, [stepIndex, surface]);
}

export function __resetClipStatsForTests() {
  win = freshWindow();
  recent = [];
  clipUrls.clear();
  try { observer?.disconnect(); } catch { /* ignore */ }
  observer = null;
  observers = 0;
  observedBefore = false;
  seq = 0;
}
